import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { runAz } from "../../lib/az.js";
import { parseContainerAppResourceId } from "../../lib/platform-update.js";
import { wireGatewayForAcs } from "../../lib/wire-gateway-acs.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../../lib/output.js";

export class PlatformEnableCostReportCommand extends M8tCommand {
  static paths = [["platform", "enable-cost-report"]];
  static usage = Command.Usage({
    description: "Wire the deployed gateway to send the bi-weekly cost report via ACS Email.",
    details:
      "Discovers the live gateway Container App, grants its managed identity Contributor at the ACS resource scope (authorising the ACS Email send), and sets M8T_ACS_ENDPOINT / M8T_ACS_SENDER / M8T_ENABLE_COST_REPORTER=1 on the gateway. Idempotent — safely re-runnable. ACS is created during the executor deploy; pass its endpoint, sender, and resource id (from that deploy or 'az communication list').",
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });
  acsEndpoint = Option.String("--acs-endpoint", { required: true, description: "ACS data-plane endpoint, e.g. https://acs-<name>.communication.azure.com/" });
  acsSender = Option.String("--acs-sender", { required: true, description: "Managed-domain sender address, e.g. DoNotReply@<guid>.azurecomm.net" });
  acsResourceId = Option.String("--acs-resource-id", { required: true, description: "ARM resource id of the acs-<name> Communication Service — the role-grant scope." });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const log = (msg: string) => {
      if (mode !== "json") this.context.stdout.write(msg + "\n");
    };

    const ctx = await resolveGatewayContext({
      interactive: mode !== "json",
      subscriptionId: this.subscription,
      resourceGroup: this.resourceGroup,
    });
    const { resourceGroup, name } = parseContainerAppResourceId(ctx.containerAppResourceId);

    const gatewayPrincipalId = (
      await runAz([
        "containerapp", "show", "-n", name, "-g", resourceGroup,
        "--query", "identity.principalId", "-o", "tsv",
      ])
    ).trim();
    if (gatewayPrincipalId === "") {
      throw new LocalCliError({
        code: "GATEWAY_NO_MANAGED_IDENTITY",
        message: `Gateway '${name}' has no managed identity principalId to grant.`,
        hint: "Assign a system-assigned identity to the gateway Container App, then re-run.",
      });
    }

    await wireGatewayForAcs({
      az: runAz,
      resourceGroup,
      acsResourceId: this.acsResourceId,
      gatewayApp: name,
      endpoint: this.acsEndpoint,
      sender: this.acsSender,
      gatewayPrincipalId,
    });

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          gateway: name,
          resourceGroup,
          roleGranted: "Contributor",
          scope: this.acsResourceId,
          envSet: {
            M8T_ACS_ENDPOINT: this.acsEndpoint,
            M8T_ACS_SENDER: this.acsSender,
            M8T_ENABLE_COST_REPORTER: "1",
          },
        }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "gateway", value: name },
        { key: "role granted", value: `Contributor @ ${this.acsResourceId}` },
        { key: "M8T_ACS_ENDPOINT", value: this.acsEndpoint },
        { key: "M8T_ACS_SENDER", value: this.acsSender },
        { key: "M8T_ENABLE_COST_REPORTER", value: "1" },
      ]) + "\n",
    );
    log(colors.success("cost report enabled — the gateway will send it via ACS ✓"));
    return 0;
  }
}
